import './leaderboard-page.scss';

import { Loader2 } from 'lucide-react';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  leaderboardActions,
  selectLeaderboardEntries,
  selectLeaderboardGameType,
  selectLeaderboardHasMore,
  selectLeaderboardInitialLoading,
  selectLeaderboardLoading,
  selectLeaderboardMyRank,
  selectLeaderboardPeriod,
  selectLeaderboardSortBy,
} from '@/entities/leaderboard';
import type { GameTypeValue } from '@/shared/config/constants';
import { useTelegramBackButton } from '@/shared/lib/telegram/useTelegramBackButton';

import {
  LeaderboardFilters,
  LeaderboardItem,
  LeaderboardMyRank,
  LeaderboardSearch,
  LeaderboardSkeleton,
  LeaderboardSortTabs,
  LeaderboardTabs,
} from './ui';

export const LeaderboardPage = () => {
  useTelegramBackButton();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const entries = useAppSelector(selectLeaderboardEntries);
  const hasMore = useAppSelector(selectLeaderboardHasMore);
  const loading = useAppSelector(selectLeaderboardLoading);
  const initialLoading = useAppSelector(selectLeaderboardInitialLoading);
  const period = useAppSelector(selectLeaderboardPeriod);
  const sortBy = useAppSelector(selectLeaderboardSortBy);
  const myRank = useAppSelector(selectLeaderboardMyRank);
  const gameType = useAppSelector(selectLeaderboardGameType);

  const [searchValue, setSearchValue] = useState('');

  const handleGameTabChange = useCallback(
    (value: GameTypeValue) => {
      dispatch(leaderboardActions.resetEntries());
      dispatch(leaderboardActions.setGameType(value));
      setSearchValue('');
    },
    [dispatch],
  );

  const handlePeriodChange = useCallback(
    (v: Parameters<typeof leaderboardActions.setPeriod>[0]) =>
      dispatch(leaderboardActions.setPeriod(v)),
    [dispatch],
  );

  const handleSortChange = useCallback(
    (v: Parameters<typeof leaderboardActions.setSortBy>[0]) =>
      dispatch(leaderboardActions.setSortBy(v)),
    [dispatch],
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          dispatch(leaderboardActions.fetchNextPage());
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, dispatch],
  );

  useEffect(
    () => () => {
      observer.current?.disconnect();
    },
    [],
  );

  const filteredEntries = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) => (e.username ?? '').toLowerCase().includes(q));
  }, [entries, searchValue]);

  const isSearching = searchValue.trim().length > 0;
  const showEmpty = !initialLoading && !loading && filteredEntries.length === 0;
  const myEntryInList = myRank?.entry
    ? filteredEntries.find((e) => e.userId === myRank.entry!.userId)
    : null;
  const showStickyFooter = !isSearching && myRank?.rank != null && !myEntryInList;

  return (
    <div className="leaderboard-page">
      <LeaderboardTabs active={gameType} onChange={handleGameTabChange} />

      <div className="leaderboard-page__fade-in" style={{ '--fade-in-delay': '0.05s' } as CSSProperties}>
        <LeaderboardFilters active={period} onChange={handlePeriodChange} />
      </div>

      <div className="leaderboard-page__fade-in" style={{ '--fade-in-delay': '0.08s' } as CSSProperties}>
        <LeaderboardSortTabs active={sortBy} onChange={handleSortChange} />
      </div>

      <div className="leaderboard-page__fade-in" style={{ '--fade-in-delay': '0.1s' } as CSSProperties}>
        <LeaderboardSearch value={searchValue} onChange={setSearchValue} />
      </div>

      {initialLoading ? (
        <div className="leaderboard-page__list">
          <LeaderboardSkeleton />
        </div>
      ) : filteredEntries.length > 0 ? (
        <div className="leaderboard-page__list">
          {filteredEntries.map((entry, idx) => (
            <LeaderboardItem key={entry.userId} entry={entry} sortBy={sortBy} index={idx} />
          ))}
          {!isSearching && hasMore && (
            <div ref={sentinelRef} className="leaderboard-page__sentinel">
              {loading && <Loader2 className="leaderboard-page__spinner" />}
            </div>
          )}
        </div>
      ) : showEmpty ? (
        <p className="leaderboard-page__empty leaderboard-page__empty--fade-in">
          {isSearching ? t('leaderboard.noPlayersFound') : t('leaderboard.noDataYet')}
        </p>
      ) : null}

      {showStickyFooter && myRank?.entry && (
        <LeaderboardMyRank rank={myRank.rank!} entry={myRank.entry} sortBy={sortBy} />
      )}
    </div>
  );
};
